import { Controller } from "@hotwired/stimulus"

// The UPG panels on the convert form — "UPG contacts nearby" and, beneath it,
// "Top Sites in this group": mounts them when the rep chooses an Ultimate Parent Group, and
// re-mounts them whenever they change their mind.
//
// The panels are ABSENT until a group is chosen — the form must not carry an
// empty box the rep cannot fill — so their frames ship inside an inert
// <template> and are cloned into place on the first choice. A <template>'s
// contents are not in the document, so Turbo never sees the frames and nothing
// is fetched on open.
//
// REPLACED, never `src`-rewritten, for the two reasons the map card's contacts
// panel is replaced rather than rewritten: Turbo re-fetches a frame the moment
// its src changes once it has loaded even once, and the cards already in the
// frame belong to the PREVIOUS group — they would sit under the new group's
// heading until the response landed. A fresh lazy frame has never loaded, so it
// fetches as soon as it is in the document and visible, and the rep sees the
// placeholder rather than somebody else's group.
export default class extends Controller {
  static targets = ["panel", "template"]
  // The group a refused form came back with, already chosen in the picker.
  static values = { chosen: String }

  connect() {
    if (this.chosenValue) this._mount(this.chosenValue)
  }

  // Bound to `ui--entity-selector:change`, the group picker's OWN event, never
  // to a native `change` bubbling out of it: the picker's search text box fires
  // one of those too when the rep leaves it, carrying whatever they typed, and
  // treating that as a group id tore down the chosen group's cards.
  groupChanged(event) {
    // The previous group's cards go FIRST and unconditionally — from every
    // panel at once. Leaving them standing for the length of a request is how a
    // rep reads five accounts as belonging to a group they are not in.
    this.panelTarget.replaceChildren()

    const groupId = event.detail?.value
    // Cleared back to the placeholder: no group, no panels, exactly as on load.
    if (!groupId) return

    this._mount(groupId)
  }

  _mount(groupId) {
    const mounted = this.templateTarget.content.cloneNode(true)

    // Each frame names its OWN endpoint, built by the server — it is
    // space-scoped, and the nearby panel's already carries the discovered
    // site's coordinates while the winners' deliberately carries none.
    // Rebuilding any of that here would break the moment a route changes, so
    // only the group the rep just picked is added. `setAttribute` on the
    // DETACHED clone, so nothing can fetch before it is in the document.
    mounted
      .querySelectorAll("turbo-frame[data-upg-panel-src]")
      .forEach((frame) => {
        const url = new URL(frame.dataset.upgPanelSrc, window.location.origin)
        url.searchParams.set("group_id", groupId)
        frame.setAttribute("src", `${url.pathname}${url.search}`)
      })

    this.panelTarget.appendChild(mounted)
  }
}
